// Catalogue des rubriques et tarifs (H4-03) : une carte par rubrique avec ses tarifs datés ;
// proposition et modification d'un tarif proposé par le financier, approbation ou suspension par
// le maire ou président (quatre yeux : jamais la personne qui a proposé), tarif approuvé figé ;
// création et modification des rubriques ; simulation de tarification.
import { Bouton, EtatVide, Filtres, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { Calculator, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { ActionMetier } from '../metier/ActionMetier';
import { LIBELLES_BASE, LIBELLES_CATEGORIE, LIBELLES_NATURE, LIBELLES_PERIODICITE, LIBELLES_STATUT_TARIF, LIBELLES_UNITE_TEMPS, libelle, tarifEnVigueur, texteTarif, tonTarif, type RubriqueReponse, type Tarif } from './donnees';
import { FormulaireRubrique, FormulaireTarif, SimulationTarification } from './FormulairesCatalogue';
import { NavigationFinances, useDroitsRecettes, type DroitsRecettes } from './NavigationFinances';
import { INVALIDER_RECETTES, useRubriques } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

const INVALIDER = [...INVALIDER_RECETTES];

function ActionsTarif({ tarif, droits, onModifier }: { tarif: Tarif; droits: DroitsRecettes; onModifier: () => void }) {
  const mienne = droits.moi !== null && tarif.propose_par === droits.moi;
  return (
    <span className={styles.actions}>
      {tarif.statut === 'propose' && droits.creer ? (
        <Bouton petit variante="discret" icone={Pencil} onClick={onModifier} aria-label={`Modifier le tarif proposé du ${date(tarif.debut)}`}>
          Modifier
        </Bouton>
      ) : null}
      {tarif.statut === 'propose' && droits.decider ? (
        <ActionMetier
          libelle="Approuver"
          titre="Approuver le tarif"
          explication={mienne ? 'Vous avez proposé ce tarif : la règle des quatre yeux réserve son approbation à une autre personne (le serveur refusera).' : 'Le tarif devient applicable à sa date de début ; il ne sera plus modifiable. Un tarif approuvé sans référence d\'acte ouvre une anomalie.'}
          chemin={`/recettes/tarifs/${tarif.id}/approuver`}
          champs={[{ nom: 'acte_reference', libelle: 'Référence de l\'acte', aide: 'Délibération ou arrêté, si elle n\'a pas été saisie à la proposition' }]}
          succes="Tarif approuvé."
          invalider={INVALIDER}
          variante="principal"
        />
      ) : null}
      {tarif.statut === 'approuve' && droits.decider ? (
        <ActionMetier libelle="Suspendre" titre="Suspendre le tarif" explication="Le tarif n'est plus applicable ; la suspension est motivée et journalisée." chemin={`/recettes/tarifs/${tarif.id}/suspendre`} champs={[{ nom: 'motif', libelle: 'Motif', obligatoire: true, multiligne: true }]} succes="Tarif suspendu." invalider={INVALIDER} variante="secondaire" />
      ) : null}
    </span>
  );
}

function CarteRubrique({ rubrique, droits, liees, onModifier, onProposer, onModifierTarif }: { rubrique: RubriqueReponse; droits: DroitsRecettes; liees: RubriqueReponse[]; onModifier: () => void; onProposer: () => void; onModifierTarif: (t: Tarif) => void }) {
  const vigueur = tarifEnVigueur(rubrique);
  const colonnes: ColonneTableau<Tarif>[] = [
    { id: 'tarif', entete: 'Tarif', enTeteLigne: true, masquable: false, cellule: (t) => texteTarif(t) },
    { id: 'temps', entete: 'Unité de temps', cellule: (t) => libelle(LIBELLES_UNITE_TEMPS, t.unite_temps) },
    { id: 'debut', entete: 'Début', cellule: (t) => date(t.debut), valeurTri: (t) => t.debut },
    { id: 'fin', entete: 'Fin', cellule: (t) => (t.fin ? date(t.fin) : 'sans fin'), valeurTri: (t) => t.fin ?? null },
    { id: 'statut', entete: 'Statut', cellule: (t) => <PuceEtat ton={tonTarif(t.statut)} libelle={libelle(LIBELLES_STATUT_TARIF, t.statut)} />, valeurTri: (t) => t.statut },
    { id: 'acte', entete: 'Acte', cellule: (t) => t.acte_reference ?? 'sans acte' },
    { id: 'approbation', entete: 'Approbation', cellule: (t) => (t.approuve_le ? `le ${date(t.approuve_le)}` : t.statut === 'propose' ? 'à approuver par le maire ou président' : 'non renseigné') },
    { id: 'actions', entete: 'Actions', masquable: false, cellule: (t) => <ActionsTarif tarif={t} droits={droits} onModifier={() => onModifierTarif(t)} /> },
  ];
  return (
    <article className={`${styles.rubrique} ${rubrique.active ? '' : styles.inactive}`} aria-labelledby={`rubrique-${rubrique.id}`}>
      <div className={styles.entre}>
        <div>
          <h3 id={`rubrique-${rubrique.id}`}>
            {rubrique.libelle} <span className={styles.code}>{rubrique.code}</span>
          </h3>
          <p className={styles.sous}>
            {libelle(LIBELLES_NATURE, rubrique.nature)}, {libelle(LIBELLES_PERIODICITE, rubrique.periodicite).toLowerCase()}, base {libelle(LIBELLES_BASE, rubrique.base_calcul).toLowerCase()} ; {rubrique.paiement_partiel_autorise ? 'paiement partiel admis' : 'paiement partiel refusé'} ; {rubrique.remboursable ? 'remboursable' : 'non remboursable'} ; fondement : {rubrique.fondement}
          </p>
        </div>
        <span className={styles.actions}>
          {!rubrique.active ? <PuceEtat ton="neutre" libelle="Inactive" /> : vigueur ? <PuceEtat ton="ok" libelle={`En vigueur : ${texteTarif(vigueur)}`} /> : <PuceEtat ton="attente" libelle="Aucun tarif approuvé en vigueur" />}
          {droits.modifier ? (
            <Bouton petit variante="secondaire" icone={Pencil} onClick={onModifier}>
              Modifier
            </Bouton>
          ) : null}
          {droits.creer && rubrique.categorie === 'principale' ? (
            <Bouton petit variante="secondaire" icone={Plus} onClick={onProposer}>
              Proposer un tarif
            </Bouton>
          ) : null}
        </span>
      </div>
      {rubrique.tarifs.length === 0 ? <p className={commun.na}>Aucun tarif pour cette rubrique.</p> : <Tableau legende={`Tarifs de ${rubrique.libelle}`} colonnes={colonnes} lignes={rubrique.tarifs} cleLigne={(t) => t.id} />}
      {liees.length > 0 ? (
        <p className={styles.sous}>
          Rubriques liées : {liees.map((l) => `${l.libelle} (${libelle(LIBELLES_CATEGORIE, l.categorie).toLowerCase()}, ${l.active ? 'active' : 'désactivée'})`).join(' ; ')}
        </p>
      ) : null}
    </article>
  );
}

export function PageCatalogue() {
  const droits = useDroitsRecettes();
  const rubriques = useRubriques();
  const [filtres, setFiltres] = useFiltresPersistes('recettes-catalogue', { actives: 'oui', tarifs: '' });
  const [fenetre, setFenetre] = useState<{ type: 'rubrique'; rubrique: RubriqueReponse | null } | { type: 'tarif'; rubrique: RubriqueReponse; tarif: Tarif | null } | { type: 'simulation' } | null>(null);
  const toutes = rubriques.data ?? [];
  const principales = toutes.filter((r) => r.categorie === 'principale');
  const visibles = principales.filter((r) => (filtres.actives === 'oui' ? r.active : true)).filter((r) => (filtres.tarifs === 'proposes' ? r.tarifs.some((t) => t.statut === 'propose') : true));
  const proposes = toutes.flatMap((r) => r.tarifs).filter((t) => t.statut === 'propose').length;

  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Catalogue et tarifs"
        description="Rubriques des taxes, redevances, droits et frais ; tarifs datés proposés par le financier et approuvés par le maire ou président. Les tarifs de présentation portent la mention « (fictif) »."
        actions={
          <>
            {droits.voir ? (
              <Bouton variante="secondaire" icone={Calculator} onClick={() => setFenetre({ type: 'simulation' })}>
                Simuler une tarification
              </Bouton>
            ) : null}
            {droits.creer ? (
              <Bouton icone={Plus} onClick={() => setFenetre({ type: 'rubrique', rubrique: null })}>
                Nouvelle rubrique
              </Bouton>
            ) : null}
          </>
        }
      />
      <NavigationFinances />
      <Filtres
        libelle="Filtres du catalogue"
        groupes={[
          { id: 'actives', libelle: 'Rubriques', options: [{ valeur: 'oui', libelle: 'Actives' }, { valeur: '', libelle: 'Toutes' }] },
          { id: 'tarifs', libelle: 'Tarifs', options: [{ valeur: '', libelle: 'Tous' }, { valeur: 'proposes', libelle: `À approuver${proposes > 0 ? ` (${proposes})` : ''}` }] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={rubriques.isPending} erreur={rubriques.error} onReessayer={() => void rubriques.refetch()} lot="L2-A" fonction="Le catalogue des rubriques">
        {visibles.length === 0 ? (
          <EtatVide titre="Aucune rubrique">
            <p>{toutes.length === 0 ? 'Le catalogue est vide : créez une rubrique puis proposez-lui un tarif.' : 'Aucune rubrique ne correspond à ces filtres.'}</p>
          </EtatVide>
        ) : (
          <div className={styles.rubriques}>
            {visibles.map((r) => (
              <CarteRubrique key={r.id} rubrique={r} droits={droits} liees={toutes.filter((l) => l.rubrique_parent_id === r.id)} onModifier={() => setFenetre({ type: 'rubrique', rubrique: r })} onProposer={() => setFenetre({ type: 'tarif', rubrique: r, tarif: null })} onModifierTarif={(t) => setFenetre({ type: 'tarif', rubrique: r, tarif: t })} />
            ))}
          </div>
        )}
      </EtatRequete>
      {fenetre?.type === 'rubrique' ? <FormulaireRubrique key={fenetre.rubrique?.id ?? 'nouvelle'} ouvert onFermer={() => setFenetre(null)} rubrique={fenetre.rubrique} principales={principales} /> : null}
      {fenetre?.type === 'tarif' ? <FormulaireTarif key={fenetre.tarif?.id ?? `nouveau-${fenetre.rubrique.id}`} ouvert onFermer={() => setFenetre(null)} rubrique={fenetre.rubrique} tarif={fenetre.tarif} /> : null}
      {fenetre?.type === 'simulation' ? <SimulationTarification ouvert onFermer={() => setFenetre(null)} rubriques={toutes} /> : null}
    </>
  );
}
